import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import type { ReactNode } from 'react'
import { NavLink } from 'react-router'
import { tepiGulir } from '@/components/primitives/tepiGeser'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { LG_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import { useUi } from '../store/ui'
import type { NavGroup, NavItem } from './nav'

/** The persisted preference, or the breakpoint when nobody has chosen: open from 1024 px. */
export function useSidebarOpen(): [boolean, (open: boolean) => void] {
  const isLg = useMediaQuery(LG_QUERY)
  const pref = useUi((s) => s.sidebarOpen)
  const setSidebarOpen = useUi((s) => s.setSidebarOpen)
  return [pref ?? isLg, setSidebarOpen]
}

const ITEM =
  'relative flex h-9 items-center gap-2 text-table text-text hover-band press focus-settle-inset pointer-coarse:h-11 aria-[current=page]:font-medium aria-[current=page]:before:absolute aria-[current=page]:before:top-1.5 aria-[current=page]:before:bottom-1.5 aria-[current=page]:before:left-0 aria-[current=page]:before:w-0.5 aria-[current=page]:before:bg-primary'

function Item({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon
  const icon = <Icon size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
  if (!collapsed) {
    return (
      <li>
        <NavLink to={item.to} end={item.end ?? false} className={cn(ITEM, 'px-4')}>
          {icon}
          <span className="truncate">{item.label}</span>
        </NavLink>
      </li>
    )
  }
  return (
    <li>
      <Tooltip>
        <TooltipTrigger
          render={
            <NavLink
              to={item.to}
              end={item.end ?? false}
              aria-label={item.label}
              className={cn(ITEM, 'justify-center px-0')}
            />
          }
        >
          {icon}
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    </li>
  )
}

/** Akun with the person's name and role beneath; the rail keeps the icon, named Akun. */
function ItemAkun({
  item,
  orang,
  collapsed,
}: {
  item: NavItem
  orang: SidebarProps['orang']
  collapsed: boolean
}) {
  if (collapsed) return <Item item={item} collapsed />
  const Icon = item.icon
  return (
    <li>
      <NavLink to={item.to} className={cn(ITEM, 'h-auto min-h-11 px-4 py-1')}>
        <Icon size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
        <span className="flex min-w-0 flex-col leading-4">
          <span className="sr-only">{item.label}</span>
          <span className="truncate font-medium">{orang.nama}</span>
          <span className="truncate text-label text-muted">{orang.peran}</span>
        </span>
      </NavLink>
    </li>
  )
}

interface SidebarProps {
  groups: readonly NavGroup[]
  /** The panel's bottom (plan 9b, 5.3): Akun, and Pengaturan for admin paroki (`navAkunForRole`). */
  akun: readonly NavItem[]
  /** Who is signed in, shown on Akun: the name and the role's label. */
  orang: { nama: string; peran: string }
  /** Plan 10: what heads the panel (an admin keuskupan's "Bekerja di"); `ringkas` on the rail. */
  atas?: (ringkas: boolean) => ReactNode
}

/**
 * Rak Buku: the role-filtered groups on a surface band with a hairline right rule. The
 * active item carries the 2 px red tick; the rail (56 px) keeps icons with tooltips. Width
 * switches, it does not animate. Hidden under 768 px, where the bottom bar takes over. Its
 * bottom holds Akun (the person's name and role) and, for admin paroki, Pengaturan, above
 * Perkecil navigasi (plan 9b). An admin keuskupan's "Bekerja di" heads it (plan 10, `atas`).
 */
export function Sidebar({ groups, akun, orang, atas }: SidebarProps) {
  const [open, setOpen] = useSidebarOpen()
  return (
    <nav
      aria-label="Navigasi utama"
      className={cn(
        'vt-sidebar sticky top-12 hidden h-[calc(100dvh-3rem)] shrink-0 flex-col border-r border-border bg-surface md:flex',
        open ? 'w-60' : 'w-14',
      )}
    >
      {atas === undefined ? null : <div className="border-b border-border">{atas(!open)}</div>}
      <div ref={tepiGulir} className="gulir-bawah min-h-0 flex-1 py-2">
        {groups.map((group, index) => {
          const id = group.label === null ? undefined : `nav-${index}`
          return (
            <div
              key={group.label ?? 'beranda'}
              className={cn(index > 0 && !open && 'mt-2 border-t border-border pt-2')}
            >
              {group.label !== null && open ? (
                <p id={id} className="px-4 pt-4 pb-1 text-label font-medium text-muted">
                  {group.label}
                </p>
              ) : null}
              <ul
                aria-labelledby={open ? id : undefined}
                aria-label={open ? undefined : (group.label ?? undefined)}
              >
                {group.items.map((item) => (
                  <Item key={item.to} item={item} collapsed={!open} />
                ))}
              </ul>
            </div>
          )
        })}
      </div>
      {akun.length > 0 ? (
        <ul className="border-t border-border py-2">
          {akun.map((item) =>
            item.to === '/akun' ? (
              <ItemAkun key={item.to} item={item} orang={orang} collapsed={!open} />
            ) : (
              <Item key={item.to} item={item} collapsed={!open} />
            ),
          )}
        </ul>
      ) : null}
      <div className="border-t border-border p-2">
        {open ? (
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-expanded="true"
            aria-label="Perkecil navigasi"
            className="flex h-9 w-full items-center gap-2 px-2 text-table text-muted hover-band press focus-settle-inset pointer-coarse:h-11"
          >
            <PanelLeftClose size={20} strokeWidth={1.75} aria-hidden="true" />
            <span>Perkecil navigasi</span>
          </button>
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  aria-expanded="false"
                  aria-label="Perbesar navigasi"
                  className="flex h-9 w-10 items-center justify-center text-muted hover-band press focus-settle-inset pointer-coarse:h-11"
                />
              }
            >
              <PanelLeftOpen size={20} strokeWidth={1.75} aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent side="right">Perbesar navigasi</TooltipContent>
          </Tooltip>
        )}
      </div>
    </nav>
  )
}
